import React, { useEffect, useState } from "react";
import {
  Banknote,
} from "lucide-react"; // Import icons
import { Button } from "@/components/ui/button";
import { useAppStore } from "@/hooks/useAppStore";
import { ShareRoomDialog } from "../dialogs/ShareRoomDialog";
import { RoomHistoryDialog } from "../dialogs/RoomHistoryDialog";
import { TokenBalanceDialog } from "../dialogs/TokenBalanceDialog";
import { JoinRoomDialog } from "../dialogs/JoinRoomDialog";
import { cn } from "@/lib/utils";
import { useTranslation } from "@/lib/translate";
import { useNavigate } from "react-router-dom"; // For navigation
import { useTheme } from "../themes/ThemeProvider";

export const Header: React.FC = () => {
  const navigate = useNavigate();
  const { resolvedTheme } = useTheme();
  const roomId = useAppStore((state) => state.roomId);
  const user = useAppStore((state) => state.user);
  const wallet = useAppStore((state => state.balance));
  const preferredLanguage = useAppStore((state) => state.preferredLanguage);
  const isPresentationMode = useAppStore((state) => state.isPresentationMode);
  const setPresentationMode = useAppStore((state) => state.setPresentationMode);

  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [roomsHistoryDialogOpen, setRoomsHistoryDialogOpen] = useState(false);
  const [tokenbalanceDialogOpen, setTokenBalanceDialogOpen] = useState(false);
  const [roomDropdownOpen, setRoomDropdownOpen] = useState(false);
  const [joinDialogOpen, setJoinDialogOpen] = useState(false);

  const t = useTranslation(preferredLanguage);

  const LOW_BALANCE = 250;
  const GOOD_BALANCE = 700;

    const handleCreateRoom = async () => {
    if (window.confirm(t("Create a new chat room? This will leave your current room."))) {
      try {
        navigate(`/new-room?r=${roomId}`, { replace: true });
      } catch (error) {
        console.error(t("Failed to initiate room creation:"), error);
      }
    }
  };

  //Toggle split screen mode
  const togglePresentationMode = () => {
    setPresentationMode(!isPresentationMode);
  }

  const balance = wallet && wallet.free || 0;
  const balanceIconColor = (balance < LOW_BALANCE ? "text-red-500" : (balance > GOOD_BALANCE ? "text-green-300" : "text-orange-400"));
  const isGuest = user && user.provider == "internal";

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Ignore key events from input fields or textareas
      const target = e.target as HTMLElement;
      const isTypingArea = ['INPUT', 'TEXTAREA'].includes(target.tagName);
      if (isTypingArea || target.isContentEditable) return;

      if (e.code === "F5") {
        e.preventDefault(); // Prevent page scroll
        togglePresentationMode();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [togglePresentationMode]);

  const clientConfig = useAppStore((state) => state.clientConfig);

  const logoSrc = resolvedTheme === 'dark' && clientConfig?.config?.logoDark
    ? clientConfig.config.logoDark
    : clientConfig?.config?.logo;

  return (
    <>
      <div className={cn("flex-shrink-0 bg-chotto-header/95", isPresentationMode ? "hidden" : "")} style={{ paddingTop: 'env(safe-area-inset-top)' }}>
      <header className="relative flex h-16 items-center justify-between border-b border-neutral-500/30 px-4 w-full">
        <div>
          <div 
            className="room-id text-[8px] md:text-[13px]"
            onClick={() => setRoomDropdownOpen(!roomDropdownOpen)}
            style={{
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '6px 12px',
              background: 'rgba(77, 208, 225, 0.1)',
              borderRadius: '20px',
              fontWeight: '600',
              color: '#333',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(77, 208, 225, 0.2)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(77, 208, 225, 0.1)';
            }}
          >
            <span className="hidden sm:inline" style={{ color: 'rgba(0, 0, 0, 0.5)', fontSize: '11px' }}>{t("ROOM ID:")}</span>
            <span style={{ color: '#00ACC1' }}>{roomId || "N/A"}</span>
            <div 
              style={{
                width: 0,
                height: 0,
                borderLeft: '4px solid transparent',
                borderRight: '4px solid transparent',
                borderTop: '5px solid #666',
                marginLeft: '2px',
                transition: 'transform 0.2s ease',
                transform: roomDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)'
              }}
            />
            <div 
              style={{
                position: 'absolute',
                top: 'calc(100% + 8px)',
                left: 0,
                background: 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(20px)',
                borderRadius: '12px',
                padding: '8px',
                minWidth: '200px',
                boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)',
                display: roomDropdownOpen ? 'flex' : 'none',
                flexDirection: 'column',
                gap: '4px',
                zIndex: 1000,
              }}
            >
              <button 
                onClick={(e) => {
                  e.stopPropagation();
                  setRoomDropdownOpen(false);
                  handleCreateRoom();
                }}
                style={{
                  padding: '12px 16px',
                  background: 'transparent',
                  border: 'none',
                  color: '#333',
                  cursor: 'pointer',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  textAlign: 'left',
                  transition: 'all 0.2s ease',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(77, 208, 225, 0.2)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'transparent';
                }}
              >
                Create New Room
              </button>
              <button 
                onClick={(e) => {
                  e.stopPropagation();
                  setRoomDropdownOpen(false);
                  setJoinDialogOpen(true);
                }}
                style={{
                  padding: '12px 16px',
                  background: 'transparent',
                  border: 'none',
                  color: '#333',
                  cursor: 'pointer',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  textAlign: 'left',
                  transition: 'all 0.2s ease',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(77, 208, 225, 0.2)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'transparent';
                }}
              >
                Join Room
              </button>
              <button 
                onClick={(e) => {
                  e.stopPropagation();
                  setRoomDropdownOpen(false);
                  setRoomsHistoryDialogOpen(true);
                }}
                style={{
                  padding: '12px 16px',
                  background: 'transparent',
                  border: 'none',
                  color: '#333',
                  cursor: 'pointer',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  textAlign: 'left',
                  transition: 'all 0.2s ease',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(77, 208, 225, 0.2)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'transparent';
                }}
              >
                Past Rooms
              </button>
            </div>
          </div>
        </div>
        <img 
          src={logoSrc || clientConfig?.config?.logo} 
          width={clientConfig?.config?.logoWidth || 200} 
          className="absolute left-1/2 -translate-x-1/2 h-full w-auto object-contain max-w-[30%]"
        />
        <div className="flex items-center gap-1 md:gap-2">
          {roomId && (
              <button
                className="share-btn py-[2px] px-[12px] md:py-[8px] md:px-[16px]"
                onClick={() => setShareDialogOpen(true)}
                style={{
                  background: 'rgba(77, 208, 225, 0.9)',
                  border: '2px solid rgba(77, 208, 225, 1)',
                  borderRadius: '20px',
                  color: '#fff',
                  fontSize: '13px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  transition: 'all 0.2s ease',
                  boxShadow: '0 2px 8px rgba(77, 208, 225, 0.3)',
                  marginRight: '8px'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(77, 208, 225, 1)';
                  e.currentTarget.style.transform = 'translateY(-1px)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(77, 208, 225, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(77, 208, 225, 0.9)';
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 2px 8px rgba(77, 208, 225, 0.3)';
                }}
              >
                <svg className="share-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: '16px', height: '16px' }}>
                  <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"></path>
                  <polyline points="16 6 12 2 8 6"></polyline>
                  <line x1="12" y1="2" x2="12" y2="15"></line>
                </svg>
                <span className="hidden sm:inline">{t("Share")}</span>
              </button>
            )}
          {!isGuest &&
            <Button
              variant="ghost"
              onClick={() => setTokenBalanceDialogOpen(true)}
              className="flex items-center px-2">
              <Banknote className={`h-5 w-5 ${balanceIconColor} ml-2`} />
              <span className="hidden min-[410px]:inline px-1" style={{ fontSize: 10 }}>{wallet?.free || "..."}</span>
            </Button>}
        </div>
      </header>
      </div>

      {/* Render Dialogs */}
      <ShareRoomDialog
        open={shareDialogOpen}
        onOpenChange={setShareDialogOpen}
      />
      <RoomHistoryDialog
        open={roomsHistoryDialogOpen}
        onOpenChange={setRoomsHistoryDialogOpen}
      />
      <TokenBalanceDialog
        open={tokenbalanceDialogOpen}
        onOpenChange={setTokenBalanceDialogOpen}
      />
      <JoinRoomDialog
        open={joinDialogOpen}
        onOpenChange={setJoinDialogOpen}
      />
    </>
  );
};
